import { createElement, type FC } from 'react';
import { ExperimentOutlined } from '@ant-design/icons';
import type { RouteObject } from 'react-router-dom';

import { paths } from '@app/constants';
import { features, type FeatureName } from '@app/utils/feature-flags';

import { PrototypeBoundary } from './shared/components/PrototypeBoundary';
import { trialsV2Routes } from './trials-v2/routes';

export interface PrototypeMenuEntry {
  icon: FC;
  label: string;
  path: string;
}

// Where a prototype's route/menu entry is registered:
// - 'predicates-submenu': nested under the "Predicates" group, Admin/Reviewer only.
// - 'top-level': a standalone item next to "Insights", reachable by every B2B role.
export type PrototypePlacement = 'predicates-submenu' | 'top-level';

export interface PrototypeDef {
  feature: Exclude<FeatureName, 'prototypesEnabled'>;
  getRoutes: () => RouteObject[];
  menu: PrototypeMenuEntry[];
  placement: PrototypePlacement;
}

export const prototypes: PrototypeDef[] = [
  {
    feature: 'predicate',
    placement: 'predicates-submenu',
    menu: [{ label: 'Trials v2', path: paths.trials_v2.root, icon: ExperimentOutlined }],
    getRoutes: () => trialsV2Routes(),
  },
];

const withBoundary = (route: RouteObject): RouteObject => ({
  ...route,
  element: createElement(PrototypeBoundary, null, route.element),
});

export const enabledPrototypeRoutes = (placement: PrototypePlacement): RouteObject[] => {
  if (!features.prototypesEnabled) return [];
  return prototypes
    .filter((p) => p.placement === placement)
    .flatMap((p) => (features[p.feature] ? p.getRoutes().map(withBoundary) : []));
};

export const enabledPrototypeMenus = (
  placement: PrototypePlacement,
  arePrototypesVisible: boolean,
): PrototypeMenuEntry[] => {
  if (!features.prototypesEnabled || !arePrototypesVisible) return [];
  return prototypes
    .filter((p) => p.placement === placement)
    .flatMap((p) => (features[p.feature] ? p.menu : []));
};
